import { CategoryArticles } from './../../../../server/rpc/types/response-dto';
import { deepSearch } from '../../../../common/utils';
import { Category, Article, CoreErrorSectionConfig, Company } from '@wix/answers-api';
import { prettyTimestamp } from '@wix/answers-lib';
import { getContactOptions } from '../../../../common/contact-utils';

export function utils(model: Record<string, any>, parser: any) {
  return {
    getCategoryForArticleId: (category: string | Category): Category => {
      const categoryId = typeof category === 'string' ? category : category.id;
      return deepSearch<any, any>(
        model.categoryTree,
        'children',
        (item) => item.id === categoryId,
      );
    },
    calcReadTime: (article: Article): string => {
      try {
        const div = document.createElement('div');
        div.innerHTML = article.content;
        const textContent = div.innerText;
        const words = countWords(textContent);
        return wordNumToMinutes(words).toString();
      } catch (err) {
        const textContent = (parser as CheerioAPI)
          .load(`<html>${article.content}</html>`)('html')
          .text();
        const words = countWords(textContent);
        return wordNumToMinutes(words).toString();
      }
    },
    getFormattedDate: (date: number) => formatDate(date),
    getFormattedDateFromNow: (date: number) => prettyTimestamp(date, true),
    getContactOptions: () => getContactOptions(model.tenant),
    getTopArticles: (categoryId: string) =>
      (model.categoryArticles as CategoryArticles[]).find(
        (category: { categoryId: string; articles: any[] }) =>
          category.categoryId === categoryId,
      )?.articles || [],
    getSubcategoryArticles: (subcategoryId: string) =>
      ((model.subcategoryArticles || []) as CategoryArticles[]).find(
        (category: { categoryId: string; articles: any[] }) =>
          category.categoryId === subcategoryId,
      )?.articles || [],
    getNextArticleUri: (article: Article): string | false => {
      const categoryArticles = getCategoryArticles(
        article,
        model.parentCategoryArticles,
      );
      const index = categoryArticles?.articles.findIndex(
        ({ id }) => id === article.id,
      );

      if (
        categoryArticles &&
        index !== undefined &&
        index < categoryArticles.articles.length - 1
      ) {
        return categoryArticles.articles[index + 1].uri;
      }
      return false;
    },
    getPreviousArticleUri: (article: Article): string | false => {
      const categoryArticles = getCategoryArticles(
        article,
        model.parentCategoryArticles,
      );
      const index = categoryArticles?.articles.findIndex(
        ({ id }) => id === article.id,
      );

      if (categoryArticles && index !== undefined && index > 0) {
        return categoryArticles.articles[index - 1].uri;
      }
      return false;
    },
    getErrorPageDefaults: (): CoreErrorSectionConfig =>
      getErrorPageDefaults(model.errorType),
    getCompanyName: (companyId: string): Company | undefined =>
      model.companies.find(company => company.id === companyId)?.name,
  };
}

export function getErrorPageDefaults(
  errorType: number,
): CoreErrorSectionConfig {
  return {
    title: `error-${errorType}.title`,
    showSubtitle: true,
    subtitle: `error-${errorType}.subtitle`,
    callToAction: {
      title: `error-${errorType}.cta-title`,
      uri: '/',
    },
  };
}

function getCategoryArticles(
  article: Article,
  categoryArticles: CategoryArticles[],
): CategoryArticles | undefined {
  const selectedCategoryArticles = categoryArticles.find(
    ({ categoryId }: { categoryId: string }) =>
      categoryId === article.categoryId,
  );

  const rearrangedArticles = selectedCategoryArticles?.articles.sort(
    (a, b) => a.articleType - b.articleType,
  );
  return (
    selectedCategoryArticles && {
      ...selectedCategoryArticles,
      articles: rearrangedArticles || [],
    }
  );
}

function countWords(str: string): number {
  return str.trim().split(/\s+/).length;
}

function wordNumToMinutes(numberOfWords: number): number {
  const WORDS_PER_MINUTE = 200;
  return Math.ceil(numberOfWords / WORDS_PER_MINUTE) || 1;
}
export function formatDate(date: number): string {
  return new Date(date).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
